"use server";

import prisma from "@/lib/prisma";
import { revalidatePath } from "next/cache";

export type FormState = {
	success: boolean;
	error: string | undefined;
};

async function getDataFromFormData(
	formData: FormData,
	updatedLessonId?: string,
) {
	const inputDate: string = formData.get("date") as string;
	const inputStartHours: string = formData.get("startHours") as string;

	const startTime: Date = new Date(
		inputDate + "T" + inputStartHours + ":00.000+09:00",
	);

	const minutes: number = parseInt(formData.get("minutes") as string, 10);

	const targetDate = new Date(startTime);
	targetDate.setHours(0, 0, 0, 0);
	const targetNextDate = new Date(targetDate);
	targetNextDate.setDate(targetDate.getDate() + 1);

	const lessonsAtTargetDate = await prisma.lesson.findMany({
		where: {
			id: {
				not: updatedLessonId, // Exclude the lesson being updated
			},
			startTime: {
				gte: targetDate,
				lt: targetNextDate,
			},
		},
	});

	const isExisted = lessonsAtTargetDate.some((lesson) => {
		const lessonStartTime = lesson.startTime;
		return lessonStartTime.getTime() === startTime.getTime();
	});

	if (isExisted) {
		return { error: "その時間の授業はすでに存在しています。" };
	}

	const inputGrades: string[] = formData.getAll("grades") as string[];
	const students: number[] = gradesToStudents(inputGrades);
	const wage = calculateWage(students, minutes);

	return {
		data: {
			startTime,
			minutes,
			students,
			wage,
		},
	};
}

export async function getAllLessons() {
	const allLessons = await prisma.lesson.findMany({
		orderBy: { startTime: "asc" },
	});
	return allLessons;
}

export async function addLesson(_prevState: FormState, formData: FormData) {
	const res = await getDataFromFormData(formData);

	if ("error" in res) {
		return {
			success: false,
			error: res.error,
		};
	}

	await prisma.lesson.create({
		data: res.data,
	});

	revalidatePath("/");

	return { success: true, error: undefined };
}

export async function updateLesson(_prevState: FormState, formData: FormData) {
	const id = formData.get("id") as string;

	const res = await getDataFromFormData(formData, id);

	if ("error" in res) {
		return {
			success: false,
			error: res.error,
		};
	}

	await prisma.lesson.update({
		where: { id: id },
		data: res.data,
	});

	revalidatePath("/");

	return { success: true, error: undefined };
}

export async function deleteLesson(id: string) {
	await prisma.lesson.delete({ where: { id: id } });
	revalidatePath("/");
}

function gradesToStudents(grades: string[]): number[] {
	function gradeToIdx(grade: string): number {
		switch (grade) {
			case "小学生":
				return 0;
			case "中学生":
				return 1;
			case "高校生":
				return 2;
			default:
				return -1;
		}
	}
	const students: number[] = [0, 0, 0];
	grades.forEach((grade) => {
		students[gradeToIdx(grade)]++;
	});
	return students;
}

function calculateWage(students: number[], minutes: number): number {
	const wageBase = 1100;
	const gradeAddition = [80, 160, 400];
	const rate = 50;
	const twoAddition = 50;
	const threeAddition = 100;

	let wage = wageBase;
	let numStudents = 0;
	let flag = true;

	for (let i = students.length - 1; i >= 0; i--) {
		if (students[i] > 0 && flag) {
			wage += gradeAddition[i];
			flag = false;
		}
		numStudents += students[i];
	}

	wage = Math.round((wage * minutes) / rate);

	if (numStudents === 2) {
		wage += twoAddition;
	} else if (numStudents === 3) {
		wage += threeAddition;
	}

	return wage;
}
